import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';
import defaultImg from '../../../asset/project/default_1.png';
import { WorkData } from '../../../shared/dto/EntityRepository';
import { useOptimizedImageUrl } from '../../../shared/hooks/useOptimizedImageUrl';
import { useWorkPropertyVisible } from '../../../shared/hooks/useApi/useWorkPropertyVisible';
import { useWorkContentModeStore } from '../../../shared/store/workContentModeStore';
import { shrinkString } from '../../../shared/util/shrinkString';
import {
  resolveWorkTitle,
} from '../../../shared/util/workDisplayText';
import { resolveWorkArtistName } from '../../../shared/util/workArtist';
import BlockWithVisible from '../BlockWithVisible';

interface AttributedWorkInfoMobileProps {
  data: WorkData;
  handleClick: () => void;
}

const AttributedWorkInfoMobile: React.FC<AttributedWorkInfoMobileProps> = ({ data, handleClick }) => {
  const { workPropertyVisible } = useWorkPropertyVisible();
  const workContentMode = useWorkContentModeStore((state) => state.workContentMode);
  const imgContainerRef = useRef<HTMLDivElement>(null);
  const imageUrl = useOptimizedImageUrl(data.uploadFile?.originUrl ?? '', imgContainerRef);

  const engVisible = workPropertyVisible?.engVisible !== false;
  const displayTitle = useMemo(
    () => resolveWorkTitle(data, workContentMode, engVisible),
    [data, workContentMode, engVisible],
  );
  const displayArtist = useMemo(
    () => resolveWorkArtistName(data.artist, workContentMode, engVisible),
    [data.artist, workContentMode, engVisible],
  );
  return (
    <WorkInfoComp onClick={handleClick} $isPublic={data.isPublic !== false}>
      <ImageBlock>
        <ImgWrapper ref={imgContainerRef}>
          <ThumbnailImg src={imageUrl === '' ? defaultImg : imageUrl} alt={displayTitle} />
        </ImgWrapper>
      </ImageBlock>
      <ArtistBlock>
        <ArtistText>
          {displayArtist ? shrinkString(displayArtist, 20) : '-'}
        </ArtistText>
      </ArtistBlock>
      <BlockWithVisible width="2.5" value={shrinkString(displayTitle, 20) || '-'} />
      <BlockWithVisible
        width="1.1"
        value={data.prodYear === '' || data.prodYear === null ? '-' : data.prodYear}
      />
    </WorkInfoComp>
  );
};

export default AttributedWorkInfoMobile;

const WorkInfoComp = styled.article<{ $isPublic: boolean }>`
  position: relative;
  display: flex;
  width: 100%;
  height: 38px;
  min-height: 38px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
  opacity: ${({ $isPublic }) => (!$isPublic) ? 0.6 : 1};
`;

const ImageBlock = styled.div`
  height: 100%;
  flex: 0.6;
  min-width: 40px;
  max-width: 50px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const ImgWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 84%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-right: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const ThumbnailImg = styled.img`
  width: 80%;
  height: 96%;
  object-fit: contain;
`;

const ArtistBlock = styled.div`
  flex: 1.5;
  min-width: 0;
  display: flex;
  align-items: center;
  padding-left: 6px;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const ArtistText = styled.div`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
